import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import ExcelJS from 'exceljs';
import logo from '../logo2.png';
import SidebarNav from "./SidebarNav";

const GenerateReport = () => {
    const [transactions, setTransactions] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [reportType, setReportType] = useState('monthly'); // 新增状态
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (reportType === 'monthly' && selectedMonth) {
            fetchTransactions();
        } else if (reportType === 'annual' && selectedYear) {
            fetchTransactions();
        }
    }, [selectedMonth, selectedYear, reportType]);

    const fetchTransactions = async () => {
        let startISO, endISO;

        if (reportType === 'monthly') {
            if (!selectedMonth || !/^\d{4}-\d{2}$/.test(selectedMonth)) {
                setMessage('请选择一个有效的月份。');
                return;
            }

            const [year, month] = selectedMonth.split('-').map(Number);
            const startDate = new Date(year, month - 1, 1);
            const endDate = new Date(year, month, 1);

            startISO = startDate.toISOString();
            endISO = endDate.toISOString();
        } else if (reportType === 'annual') {
            if (!selectedYear || !/^\d{4}$/.test(selectedYear)) {
                setMessage('请选择一个有效的年份。');
                return;
            }

            const year = Number(selectedYear);
            const startDate = new Date(year, 0, 1);
            const endDate = new Date(year + 1, 0, 1);

            startISO = startDate.toISOString();
            endISO = endDate.toISOString();
        }

        const { data, error } = await supabase
            .from('transactions')
            .select('*, bank_account_id, bank_accounts(bank_name, account_number)')
            .gte('date', startISO)
            .lt('date', endISO);

        if (!error) {
            setTransactions(data);
            setMessage('交易数据获取成功。');
        } else {
            console.error('获取交易数据时出错:', error);
            setMessage('获取交易数据失败。');
        }
    };

    // Helper function to fetch Base64 image data
    const getBase64ImageFromURL = async (imageUrl) => {
        const res = await fetch(imageUrl);
        const blob = await res.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.replace(/^data:image\/(png|jpeg);base64,/, ''));
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const generateExcelReport = async () => {
        if ((reportType === 'monthly' && transactions.length === 0) ||
            (reportType === 'annual' && transactions.length === 0)) {
            setMessage('没有可用的交易数据生成报表。');
            return;
        }

        try {
            const logoBase64 = await getBase64ImageFromURL(logo);

            const workbook = new ExcelJS.Workbook();
            const worksheetName = reportType === 'monthly' ? `Report_${selectedMonth}` : `Report_${selectedYear}`;
            const worksheet = workbook.addWorksheet(worksheetName);

            const imageId = workbook.addImage({
                base64: logoBase64,
                extension: 'png',
            });

            worksheet.addImage(imageId, {
                tl: { col: 0, row: 0 },
                ext: { width: 200, height: 60 },
            });

            worksheet.getRow(1).height = 60;
            worksheet.addRow([]);

            let titleDate;
            if (reportType === 'monthly') {
                const [year, month] = selectedMonth.split('-').map(Number);
                const date = new Date(year, month - 1);
                titleDate = date.toLocaleString('default', { month: 'long', year: 'numeric' });
            } else {
                const year = Number(selectedYear);
                const date = new Date(year, 0, 1);
                titleDate = date.getFullYear();
            }

            const reportTitle = `Kindbridge Investments ${reportType === 'monthly' ? titleDate + ' 财务报表' : titleDate + '年财务报表'}`;
            const titleRow = worksheet.addRow([reportTitle]);
            titleRow.font = { size: 16, bold: true };
            worksheet.mergeCells(`A${titleRow.number}:G${titleRow.number}`);
            titleRow.alignment = { vertical: 'middle', horizontal: 'center' };
            worksheet.addRow([]);

            const expenseTransactions = transactions.filter(t => t.type === '支出');
            const incomeTransactions = transactions.filter(t => t.type === '收入');
            const salaryTransactions = transactions.filter(t => t.type === '工资');

            let totalExpenses = 0;
            let totalIncomes = 0;
            let totalSalaries = 0;
            let largestExpense = 0;
            let largestIncome = 0;

            const addTransactionTable = (transactionsArray, tableTitle) => {
                worksheet.addRow([tableTitle]);
                const tableTitleRow = worksheet.lastRow;
                tableTitleRow.font = { size: 14, bold: true };
                worksheet.mergeCells(`A${tableTitleRow.number}:G${tableTitleRow.number}`);
                tableTitleRow.alignment = { vertical: 'middle', horizontal: 'left' };
                worksheet.addRow([]);

                const headers = ['交易日期', '交易类型', '交易详情', '不含税总额', '税率 (%)', '税额', '总额'];
                const headerRow = worksheet.addRow(headers);

                headerRow.eachCell((cell) => {
                    cell.font = { bold: true };
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFD3D3D3' },
                    };
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' },
                    };
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                });

                const dataStartRow = worksheet.lastRow.number + 1;

                transactionsArray.forEach(transaction => {
                    let totalAmount;

                    if (transaction.type === '收入') {
                        totalAmount = transaction.amount - transaction.tax_amount;
                    } else if (transaction.type === '支出' || transaction.type === '工资') {
                        totalAmount = transaction.amount + transaction.tax_amount;
                    }

                    const row = [
                        new Date(transaction.date).toLocaleDateString(),
                        transaction.transaction_type,
                        transaction.description,
                        transaction.amount,
                        transaction.tax_percentage,
                        transaction.tax_amount,
                        totalAmount,
                    ];
                    const dataRow = worksheet.addRow(row);

                    if (transaction.type === '收入') {
                        totalIncomes += totalAmount;
                        if (transaction.amount > largestIncome) {
                            largestIncome = transaction.amount;
                        }
                    } else if (transaction.type === '支出') {
                        totalExpenses += totalAmount;
                        if (transaction.amount > largestExpense) {
                            largestExpense = transaction.amount;
                        }
                    } else if (transaction.type === '工资') {
                        totalSalaries += totalAmount;
                    }
                });

                const totalRow = worksheet.addRow([
                    '', '', '总计',
                    { formula: `SUM(D${dataStartRow}:D${worksheet.lastRow.number})` },
                    '', { formula: `SUM(F${dataStartRow}:F${worksheet.lastRow.number})` },
                    { formula: `SUM(G${dataStartRow}:G${worksheet.lastRow.number})` }
                ]);

                totalRow.eachCell((cell, colNumber) => {
                    cell.font = { bold: true };
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    if ([4, 6, 7].includes(colNumber)) {
                        cell.numFmt = '"ZAR"#,##0.00';
                    }
                    cell.border = {
                        top: { style: 'thin' },
                        bottom: { style: 'double' },
                    };
                });

                worksheet.columns.forEach(column => {
                    column.width = 20; // 调整宽度适应数据
                });

                worksheet.getColumn(1).numFmt = 'yyyy/mm/dd';
                worksheet.getColumn(4).numFmt = '"ZAR"#,##0.00';
                worksheet.getColumn(6).numFmt = '"ZAR"#,##0.00';
                worksheet.getColumn(7).numFmt = '"ZAR"#,##0.00';

                worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
                    if (rowNumber >= dataStartRow && rowNumber <= worksheet.lastRow.number) {
                        row.eachCell({ includeEmpty: false }, (cell) => {
                            cell.border = {
                                top: { style: 'thin' },
                                left: { style: 'thin' },
                                bottom: { style: 'thin' },
                                right: { style: 'thin' },
                            };
                        });
                    }
                });

                worksheet.addRow([]);
            };

            if (expenseTransactions.length > 0) {
                addTransactionTable(expenseTransactions, '支出');
            }
            if (incomeTransactions.length > 0) {
                addTransactionTable(incomeTransactions, '收入');
            }
            if (salaryTransactions.length > 0) {
                addTransactionTable(salaryTransactions, '工资');
            }

            const profitLoss = totalIncomes - totalExpenses - totalSalaries;

            const summaryStartRow = worksheet.lastRow.number + 2;
            worksheet.addRow(['总结']);
            const summaryTitleRow = worksheet.lastRow;
            summaryTitleRow.font = { size: 14, bold: true };
            worksheet.mergeCells(`A${summaryTitleRow.number}:B${summaryTitleRow.number}`);
            summaryTitleRow.alignment = { vertical: 'middle', horizontal: 'left' };
            worksheet.addRow([]);

            worksheet.getCell(`A${summaryStartRow + 1}`).value = '总收入:';
            worksheet.getCell(`B${summaryStartRow + 1}`).value = totalIncomes;
            worksheet.getCell(`B${summaryStartRow + 1}`).numFmt = '"ZAR"#,##0.00';

            worksheet.getCell(`A${summaryStartRow + 2}`).value = '总支出:';
            worksheet.getCell(`B${summaryStartRow + 2}`).value = totalExpenses;
            worksheet.getCell(`B${summaryStartRow + 2}`).numFmt = '"ZAR"#,##0.00';

            worksheet.getCell(`A${summaryStartRow + 3}`).value = '工资:';
            worksheet.getCell(`B${summaryStartRow + 3}`).value = totalSalaries;
            worksheet.getCell(`B${summaryStartRow + 3}`).numFmt = '"ZAR"#,##0.00';

            worksheet.getCell(`A${summaryStartRow + 4}`).value = '利润/亏损:';
            worksheet.getCell(`B${summaryStartRow + 4}`).value = profitLoss;
            worksheet.getCell(`B${summaryStartRow + 4}`).numFmt = '"ZAR"#,##0.00';

            worksheet.getCell(`A${summaryStartRow + 5}`).value = '单笔最大收入:';
            worksheet.getCell(`B${summaryStartRow + 5}`).value = largestIncome;
            worksheet.getCell(`B${summaryStartRow + 5}`).numFmt = '"ZAR"#,##0.00';

            worksheet.getCell(`A${summaryStartRow + 6}`).value = '单笔最大支出:';
            worksheet.getCell(`B${summaryStartRow + 6}`).value = largestExpense;
            worksheet.getCell(`B${summaryStartRow + 6}`).numFmt = '"ZAR"#,##0.00';

            for (let i = 1; i <= 6; i++) {
                const rowNumber = summaryStartRow + i;
                worksheet.getCell(`A${rowNumber}`).font = { bold: true };
                worksheet.getCell(`B${rowNumber}`).alignment = { horizontal: 'right' };
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const fileName = reportType === 'monthly'
                ? `Kindbridge_Investments_financial-report-${selectedMonth}.xlsx`
                : `Kindbridge_Investments_financial-report-${selectedYear}.xlsx`;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);

            setMessage('报表下载成功。');
        } catch (error) {
            console.error('生成报表时出错:', error);
            setMessage('生成报表失败。');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <SidebarNav />

            {/* Main Content */}
            <div className="lg:pl-64">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between">
                            <img
                                src={logo}
                                alt="Kindbridge Investments"
                                className="h-12 w-auto"
                            />
                        </div>
                        <h1 className="mt-6 text-2xl font-semibold text-gray-900">
                            生成财务报表
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">
                            生成月度或年度财务报告Excel文件
                        </p>
                    </div>

                    {/* Form Card */}
                    <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                        <div className="p-6">
                            <div className="space-y-6">
                                {/* Report Type Selection */}
                                <div>
                                    <label
                                        htmlFor="report-type-select"
                                        className="block text-sm font-medium text-gray-700 mb-2"
                                    >
                                        报告类型
                                    </label>
                                    <select
                                        id="report-type-select"
                                        value={reportType}
                                        onChange={(e) => setReportType(e.target.value)}
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    >
                                        <option value="monthly">月度报表</option>
                                        <option value="annual">年度报表</option>
                                    </select>
                                </div>

                                {/* Date Selection */}
                                {reportType === 'monthly' ? (
                                    <div>
                                        <label
                                            htmlFor="month-select"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            选择月份
                                        </label>
                                        <input
                                            id="month-select"
                                            type="month"
                                            value={selectedMonth}
                                            onChange={(e) => setSelectedMonth(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        <label
                                            htmlFor="year-select"
                                            className="block text-sm font-medium text-gray-700 mb-2"
                                        >
                                            选择年份
                                        </label>
                                        <input
                                            id="year-select"
                                            type="number"
                                            min="2000"
                                            max="2100"
                                            value={selectedYear}
                                            onChange={(e) => setSelectedYear(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        />
                                    </div>
                                )}

                                {/* Generate Button */}
                                <div>
                                    <button
                                        type="button"
                                        onClick={generateExcelReport}
                                        disabled={(reportType === 'monthly' && !selectedMonth) ||
                                            (reportType === 'annual' && !selectedYear)}
                                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
                                    >
                                        下载Excel报表
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Message Display */}
                    {message && (
                        <div className={`mt-4 p-4 rounded-md ${
                            message.includes('成功')
                                ? 'bg-green-50 text-green-800'
                                : 'bg-red-50 text-red-800'
                        }`}>
                            <p className="text-sm">{message}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GenerateReport;
